<template>
  <div v-if="showBanner" class="church-locale-banner" role="region" aria-live="polite">
    <div class="church-locale-banner__inner">
      <v-icon icon="mdi-translate" color="#A5D867" size="28" class="mr-3 flex-shrink-0" />

      <div class="church-locale-banner__text">
        <div class="church-locale-banner__title">
          {{ $t('churchLocale.bannerTitle') }}
        </div>
        <div class="church-locale-banner__message">
          {{ $t('churchLocale.bannerMessage') }}
        </div>
      </div>

      <div class="church-locale-banner__actions">
        <v-btn
          color="#A5D867"
          class="elevation-0 font-weight-bold text-black"
          :loading="saving"
          :disabled="saving"
          @click="saveLocale('en')"
        >
          {{ $t('churchLocale.english') }}
        </v-btn>
        <v-btn
          color="#ffffff"
          variant="outlined"
          class="elevation-0 font-weight-bold church-locale-banner__btn-outline"
          :loading="saving"
          :disabled="saving"
          @click="saveLocale('pt')"
        >
          {{ $t('churchLocale.portuguese') }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import db from '@/firebase/init'

const VALID_LOCALES = new Set(['en', 'pt'])

export default {
  name: 'ChurchLocaleBanner',
  data() {
    return {
      showBanner: false,
      saving: false,
      churchDocId: null,
      checkTimer: null,
      checkGeneration: 0,
    }
  },
  computed: {
    cid() {
      return this.$store.getters['AppData/cid']
    },
    roles() {
      return this.$store.getters['UserData/getRoles'] || []
    },
    user() {
      return this.$store.getters['UserData/getUser']
    },
    isChurchAdminOnly() {
      return (
        this.roles.includes('admin') &&
        !this.roles.includes('superAdmin') &&
        !this.roles.includes('helpDesk')
      )
    },
    isReadyToEvaluate() {
      return Boolean(this.user?.email && this.cid && this.roles.length)
    },
  },
  watch: {
    isReadyToEvaluate: {
      immediate: true,
      handler() {
        this.scheduleCheck()
      },
    },
    isChurchAdminOnly() {
      this.scheduleCheck()
    },
  },
  beforeUnmount() {
    if (this.checkTimer) {
      clearTimeout(this.checkTimer)
    }
  },
  methods: {
    scheduleCheck() {
      if (this.checkTimer) {
        clearTimeout(this.checkTimer)
      }
      this.checkTimer = setTimeout(() => {
        this.checkChurchLocale()
      }, 200)
    },
    async checkChurchLocale() {
      const generation = ++this.checkGeneration

      if (!this.isReadyToEvaluate || !this.isChurchAdminOnly) {
        this.showBanner = false
        this.churchDocId = null
        return
      }

      try {
        const snap = await db
          .collection('churches')
          .where('cid', '==', this.cid)
          .limit(1)
          .get()

        if (generation !== this.checkGeneration) {
          return
        }

        if (snap.empty) {
          this.showBanner = false
          this.churchDocId = null
          return
        }

        const doc = snap.docs[0]
        const data = doc.data()

        if (data.churchLocale && VALID_LOCALES.has(data.churchLocale)) {
          this.showBanner = false
          this.churchDocId = null
          return
        }

        this.churchDocId = doc.id
        this.showBanner = true
      } catch (error) {
        if (generation === this.checkGeneration) {
          console.warn('[ChurchLocaleBanner] Could not load church:', error)
        }
      }
    },
    async saveLocale(locale) {
      if (!VALID_LOCALES.has(locale) || !this.churchDocId || this.saving) {
        return
      }

      this.saving = true

      try {
        await db.collection('churches').doc(this.churchDocId).update({
          churchLocale: locale,
          churchLocaleSetAt: Date.now(),
        })

        this.$store.dispatch('AppData/updateChurchLocale', {
          cid: this.cid,
          churchLocale: locale,
        })

        this.showBanner = false
        this.churchDocId = null
      } catch (error) {
        console.error('[ChurchLocaleBanner] Failed to save churchLocale:', error)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.church-locale-banner {
  position: sticky;
  top: 0;
  z-index: 9998;
  background: linear-gradient(90deg, #592892 0%, #6b3ba8 100%);
  color: #ffffff;
  border-bottom: 3px solid #a5d867;
  box-shadow: 0 4px 12px rgba(89, 40, 146, 0.35);
}

.church-locale-banner__inner {
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 14px 20px;
}

.church-locale-banner__text {
  flex: 1;
  min-width: 0;
}

.church-locale-banner__title {
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.3;
}

.church-locale-banner__message {
  margin-top: 4px;
  font-size: 0.875rem;
  line-height: 1.35;
  opacity: 0.92;
}

.church-locale-banner__actions {
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 10px;
}

.church-locale-banner__btn-outline {
  border-color: rgba(255, 255, 255, 0.85) !important;
  color: #ffffff !important;
}

@media (max-width: 767px) {
  .church-locale-banner__inner {
    flex-direction: column;
    align-items: flex-start;
    padding: 12px 16px;
  }

  .church-locale-banner__actions {
    width: 100%;
  }

  .church-locale-banner__actions .v-btn {
    flex: 1 1 auto;
  }
}
</style>
